import { useMemo, useState } from 'react';

const adjustmentTypes = [
    { value: 'purchase', label: 'Réception' },
    { value: 'consumption', label: 'Sortie' },
    { value: 'waste', label: 'Perte' },
    { value: 'adjustment', label: 'Ajustement' },
    { value: 'return', label: 'Retour' },
];

const defaultRow = (id) => ({
    id,
    itemType: 'ingredient',
    itemId: '',
    movementType: 'adjustment',
    quantity: '',
    notes: '',
    reference: '',
});

export default function StockAdjustmentTab({ ingredients = [], consumables = [], units = [], movements = [], onSaveStockAdjustment }) {
    const [rows, setRows] = useState([defaultRow(1)]);
    const [movementDate, setMovementDate] = useState(new Date().toISOString().slice(0, 10));
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const selectableItems = useMemo(() => {
        const ingredientOptions = (ingredients || []).map((item) => ({
            id: item.id,
            itemType: 'ingredient',
            name: item.name,
            stock: Number(item.stock ?? item.stock_quantity ?? 0),
            unit: item.unit || 'unité',
        }));

        const consumableOptions = (consumables || []).map((item) => ({
            id: item.id,
            itemType: 'consumable',
            name: item.name,
            stock: Number(item.stock ?? item.stock_quantity ?? 0),
            unit: item.unit || 'unité',
        }));

        return [...ingredientOptions, ...consumableOptions];
    }, [ingredients, consumables]);

    const normalizeHistoryEntry = (movement) => {
        const itemName = movement?.stockable?.name || movement?.item || '—';
        const typeMap = {
            purchase: 'Réception',
            consumption: 'Sortie',
            waste: 'Perte',
            adjustment: 'Ajustement',
            return: 'Retour',
        };
        return {
            id: movement.id,
            date: movement.movement_date || movement.created_at || movement.date || null,
            type: typeMap[movement.type] || movement.type || 'Autre',
            itemName,
            quantity: Number(movement.quantity ?? 0),
            notes: movement.notes || movement.reference || '—',
        };
    };

    const historyEntries = useMemo(() => {
        const relevantTypes = ['purchase', 'consumption', 'waste', 'adjustment', 'return'];
        return (movements || [])
            .filter((movement) => relevantTypes.includes(String(movement.type || '').toLowerCase()))
            .sort((a, b) => new Date(b.movement_date || b.created_at || 0) - new Date(a.movement_date || a.created_at || 0))
            .slice(0, 50)
            .map(normalizeHistoryEntry);
    }, [movements]);

    const updateRow = (rowId, changes) => {
        setRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, ...changes } : row)));
    };

    const addRow = () => {
        setRows((prev) => [...prev, defaultRow(Date.now())]);
    };

    const removeRow = (rowId) => {
        setRows((prev) => (prev.length > 1 ? prev.filter((row) => row.id !== rowId) : prev));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');
        setSuccess('');

        const normalizedRows = rows.map((row) => ({
            ...row,
            itemId: Number(row.itemId),
            quantity: Number(row.quantity),
        }));

        const invalidRow = normalizedRows.find((row) => !row.itemId || !Number.isFinite(row.quantity) || row.quantity <= 0);
        if (invalidRow) {
            setError('Chaque ligne doit contenir un élément valide et une quantité positive.');
            return;
        }

        for (const row of normalizedRows) {
            const selectedItem = selectableItems.find((item) => item.itemType === row.itemType && item.id === row.itemId);
            if (!selectedItem) {
                setError('Un élément sélectionné est introuvable.');
                return;
            }
            if (['consumption', 'waste'].includes(row.movementType) && row.quantity > selectedItem.stock) {
                setError(`Stock insuffisant pour ${selectedItem.name} (${selectedItem.stock} disponible).`);
                return;
            }
        }

        try {
            setSubmitting(true);
            for (const row of normalizedRows) {
                await onSaveStockAdjustment({
                    itemType: row.itemType,
                    itemId: row.itemId,
                    movementType: row.movementType,
                    quantity: row.quantity,
                    notes: row.notes,
                    reference: row.reference,
                    movementDate,
                });
            }

            setSuccess('Ajustement de stock enregistré avec succès.');
            setRows([defaultRow(1)]);
        } catch (saveError) {
            setError(saveError?.message || 'Impossible d’enregistrer l’ajustement.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-6">
            <section className="rounded-3xl border border-blue-400 bg-blue-50/90 p-5 shadow-xl shadow-slate-950/20">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                        <p className="text-sm uppercase tracking-[0.35em] text-blue-500">Ajustement de stock</p>
                        <h2 className="text-3xl font-semibold text-blue-900">Saisir une réception, sortie ou perte</h2>
                        <p className="mt-2 text-sm text-blue-600">
                            Ajoutez des mouvements de stock manuels avec raison et référence, puis suivez les réceptions, sorties et pertes dans l’historique.
                        </p>
                    </div>
                    <div className="grid gap-3 rounded-3xl border border-blue-200 bg-white p-4 shadow-sm sm:min-w-[280px]">
                        <label className="text-sm font-medium text-blue-900">
                            Date du mouvement
                            <input
                                type="date"
                                value={movementDate}
                                onChange={(event) => setMovementDate(event.target.value)}
                                className="mt-2 w-full rounded-2xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 focus:border-blue-400 focus:outline-none"
                            />
                        </label>
                        <p className="text-sm text-blue-600">Vous pouvez saisir plusieurs ajustements à la fois. Chaque ligne est enregistrée séparément en base.</p>
                    </div>
                </div>
            </section>

            <section className="rounded-3xl border border-blue-200 bg-white p-5 shadow-xl shadow-slate-950/10">
                <form onSubmit={handleSubmit} className="space-y-4">
                    {error ? (
                        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                            {error}
                        </div>
                    ) : null}
                    {success ? (
                        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                            {success}
                        </div>
                    ) : null}

                    <div className="overflow-x-auto rounded-3xl border border-blue-200 bg-blue-50">
                        <table className="min-w-full divide-y divide-blue-200 text-left text-sm text-slate-700">
                            <thead className="bg-blue-100 text-blue-900">
                                <tr>
                                    <th className="px-4 py-3 font-medium">Type d’élément</th>
                                    <th className="px-4 py-3 font-medium">Élément</th>
                                    <th className="px-4 py-3 font-medium">Mouvement</th>
                                    <th className="px-4 py-3 font-medium">Quantité</th>
                                    <th className="px-4 py-3 font-medium">Référence</th>
                                    <th className="px-4 py-3 font-medium">Raison</th>
                                    <th className="px-4 py-3 font-medium">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-blue-100 bg-white">
                                {rows.map((row) => {
                                    const selectedItem = selectableItems.find((item) => item.itemType === row.itemType && item.id === Number(row.itemId));
                                    return (
                                        <tr key={row.id}>
                                            <td className="px-4 py-3">
                                                <select
                                                    value={row.itemType}
                                                    onChange={(event) => updateRow(row.id, { itemType: event.target.value, itemId: '' })}
                                                    className="w-full rounded-2xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 focus:border-blue-400 focus:outline-none"
                                                >
                                                    <option value="ingredient">Ingrédient</option>
                                                    <option value="consumable">Consommable</option>
                                                </select>
                                            </td>
                                            <td className="px-4 py-3">
                                                <select
                                                    value={row.itemId}
                                                    onChange={(event) => updateRow(row.id, { itemId: event.target.value })}
                                                    className="w-full rounded-2xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 focus:border-blue-400 focus:outline-none"
                                                >
                                                    <option value="">Sélectionner</option>
                                                    {selectableItems
                                                        .filter((item) => item.itemType === row.itemType)
                                                        .map((item) => (
                                                            <option key={`${item.itemType}-${item.id}`} value={item.id}>
                                                                {item.name} ({item.unit})
                                                            </option>
                                                        ))}
                                                </select>
                                            </td>
                                            <td className="px-4 py-3">
                                                <select
                                                    value={row.movementType}
                                                    onChange={(event) => updateRow(row.id, { movementType: event.target.value })}
                                                    className="w-full rounded-2xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 focus:border-blue-400 focus:outline-none"
                                                >
                                                    {adjustmentTypes.map((type) => (
                                                        <option key={type.value} value={type.value}>
                                                            {type.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="space-y-2">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.001"
                                                        value={row.quantity}
                                                        onChange={(event) => updateRow(row.id, { quantity: event.target.value })}
                                                        className="w-full rounded-2xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 focus:border-blue-400 focus:outline-none"
                                                        placeholder="Quantité"
                                                    />
                                                    {selectedItem ? (
                                                        <p className="text-xs text-blue-600">Stock actuel : {selectedItem.stock} {selectedItem.unit}</p>
                                                    ) : null}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <input
                                                    type="text"
                                                    value={row.reference}
                                                    onChange={(event) => updateRow(row.id, { reference: event.target.value })}
                                                    className="w-full rounded-2xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 focus:border-blue-400 focus:outline-none"
                                                    placeholder="Référence"
                                                />
                                            </td>
                                            <td className="px-4 py-3">
                                                <input
                                                    type="text"
                                                    value={row.notes}
                                                    onChange={(event) => updateRow(row.id, { notes: event.target.value })}
                                                    className="w-full rounded-2xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 focus:border-blue-400 focus:outline-none"
                                                    placeholder="Raison / détail"
                                                />
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <button
                                                    type="button"
                                                    onClick={() => removeRow(row.id)}
                                                    className="rounded-full border border-blue-300 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                                                >
                                                    Supprimer
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <button
                            type="button"
                            onClick={addRow}
                            className="rounded-full border border-blue-400 bg-white px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50"
                        >
                            Ajouter une ligne
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="rounded-full bg-blue-600 px-6 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
                        >
                            {submitting ? 'Enregistrement…' : 'Enregistrer l’ajustement'}
                        </button>
                    </div>
                </form>
            </section>

            <section className="rounded-3xl border border-blue-200 bg-blue-50 p-5 shadow-xl shadow-slate-950/10">
                <h3 className="text-xl font-semibold text-blue-900">Historique des derniers ajustements</h3>
                <div className="mt-4 overflow-x-auto rounded-3xl border border-blue-200 bg-white">
                    <table className="min-w-full divide-y divide-blue-200 text-left text-sm text-slate-700">
                        <thead className="bg-blue-100 text-blue-900">
                            <tr>
                                <th className="px-4 py-3">Date</th>
                                <th className="px-4 py-3">Type</th>
                                <th className="px-4 py-3">Élément</th>
                                <th className="px-4 py-3">Quantité</th>
                                <th className="px-4 py-3">Raison</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-blue-100 bg-white">
                            {historyEntries.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-4 py-6 text-center text-sm text-blue-600">
                                        Aucun mouvement d’ajustement récent.
                                    </td>
                                </tr>
                            ) : (
                                historyEntries.map((entry) => (
                                    <tr key={entry.id} className="hover:bg-blue-50">
                                        <td className="px-4 py-3">{entry.date ? new Date(entry.date).toLocaleDateString('fr-FR') : '—'}</td>
                                        <td className="px-4 py-3 font-semibold text-blue-900">{entry.type}</td>
                                        <td className="px-4 py-3">{entry.itemName}</td>
                                        <td className="px-4 py-3">{entry.quantity > 0 ? `+${entry.quantity}` : entry.quantity}</td>
                                        <td className="px-4 py-3 text-blue-600">{entry.notes}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}
